'use client';

import React from 'react';
import { ArticleCard } from './article-card';
import { type Article } from '@/lib/types';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { getCategoryUrl } from '@/lib/articles';
import { cn } from '@/lib/utils';

interface ArticleCarouselProps {
  title: string;
  articles: Article[];
  categoryId: string;
  hideCategory?: boolean;
}

/**
 * ArticleCarousel Component
 * Professional investigative stage implementation for Sports and features.
 */
export function ArticleCarousel({ title, articles, categoryId, hideCategory = true }: ArticleCarouselProps) {
  if (articles.length === 0) return null;

  const mainArticle = articles[0];
  const sideArticles = articles.slice(1, 4);

  return (
    <section className="mb-16 xl:mb-24">
      <div className="flex items-center gap-3 mb-6 py-1 px-6 md:px-0">
         <Link href={getCategoryUrl(categoryId)} className="inline-flex items-center gap-1 group">
            <h2 className="text-2xl md:text-xl font-medium capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">{title}</h2>
            <ChevronRight className="h-5 w-5 text-[hsl(var(--section-header))] group-hover:translate-x-0.5 transition-transform" />
         </Link>
      </div>
      <div className="md:bg-[hsl(var(--investigative-stage-bg))] md:p-8 lg:px-10 lg:py-14 md:rounded-[3rem] md:shadow-2xl overflow-hidden">
        <div className="p-0 md:p-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 md:gap-6">
            {/* Left Column: Feature Story */}
            <div className="h-auto lg:h-full">
              <ArticleCard 
                article={mainArticle} 
                priority={true}
                className="h-full md:bg-transparent md:shadow-none md:border-none md:p-0"
                layout="default"
                hideCategory={hideCategory}
              />
            </div>

            {/* Right Column: Three Side Stories Stacked with dividers */}
            <div className="flex flex-col divide-y divide-white/10">
              {sideArticles.map((article, index) => (
                <div key={article.id} className="py-1 md:py-2 first:pt-0 last:pb-0">
                   <ArticleCard 
                    article={article} 
                    layout="horizontal-list" 
                    className="md:border-none md:bg-transparent md:p-0 md:shadow-none" 
                    hideLede={false}
                    hideCategory={hideCategory}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
